import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { log } from 'evlog/client';
import { Bell, CheckCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Card,
  CardContent,
  Pagination,
  Spinner,
} from '@virtual-care/shadcn';
import { Loading, NotificationCard, client, useAction } from '@virtual-care/ui';

import { Header, ProfileCard } from '../components/header';

export default function Notifications() {
  const { t } = useTranslation();
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 9,
  });

  const { data, refetch, isLoading } = useQuery({
    queryKey: ['notifications', pagination],
    queryFn: () => {
      return client.request('GET /notifications', {
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      });
    },
    placeholderData: keepPreviousData,
  });

  const { mutateAsync: markAllRead, isPending: isMarkingAllRead } = useAction(
    'PUT /notifications/mark-all-read',
    { invalidate: ['GET /notifications'] },
  );

  const notifications = data?.records || [];

  if (isLoading) return <Loading />;

  return (
    <>
      <Header>
        <div />
        <ProfileCard />
      </Header>

      <div className="flex-1 space-y-6 p-4 md:pe-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-between space-x-2 space-x-reverse">
            <h1 className="text-2xl font-bold">
              {t('notifications.pageTitle')}
            </h1>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="cursor-pointer"
            disabled={notifications.length === 0 || isMarkingAllRead}
            onClick={async () => {
              log.info({ action: 'mark_all_notifications_read' });
              await markAllRead({});
              await refetch();
            }}
          >
            {isMarkingAllRead ? (
              <Spinner />
            ) : (
              <CheckCheck className="mx-2 h-4 w-4" />
            )}
            {t('notifications.markAllRead')}
          </Button>
        </div>

        <div className="space-y-4">
          {notifications.length === 0 ? (
            <Card className="bg-[#FAFAFA] py-8">
              <CardContent className="px-4">
                <div className="flex flex-col items-center justify-center text-center">
                  <div className="ring-border mb-4 flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-full bg-white ring-1">
                    <Bell className="h-8 w-8 text-gray-400" />
                  </div>
                  <h3 className="mb-2 text-lg font-medium text-gray-900">
                    {t('notifications.noNotifications')}
                  </h3>
                  <p className="text-gray-500">
                    {t('notifications.noNotificationsDescription')}
                  </p>
                </div>
              </CardContent>
            </Card>
          ) : (
            notifications.map((notification) => {
              return (
                <NotificationCard
                  key={notification.id}
                  notification={notification}
                />
              );
            })
          )}
        </div>

        <Pagination
          currentPage={data?.currentPage ? data?.currentPage - 1 : 1}
          pageCount={data?.pageCount || 0}
          onPageChange={(pageIndex) => {
            setPagination((prev) => ({
              ...prev,
              pageIndex: pageIndex,
            }));
          }}
        />
      </div>
    </>
  );
}
